package top.wcpe.allincore.dsl.ir.model.hud

import top.wcpe.allincore.dsl.ir.model.internal.ReadOnlyList
import kotlin.jvm.JvmField

/** HUD 与 screen 共存时的可见策略。 */
class HudVisibilityPolicy(
    whenScreen: String?,
    allow: List<String>?,
    deny: List<String>?,
) {

    // whenScreen 为空时回落到 VISIBILITY_HIDE，字段恒非空。
    val whenScreen: String = whenScreen ?: HudMetaDefaults.VISIBILITY_HIDE
    val allow: List<String> = copy(allow, "allow")
    val deny: List<String> = copy(deny, "deny")

    /**
     * 判断当前 screen 状态下是否应渲染本 HUD。
     *
     * 策略 1=A：vanilla Screen / UIRuntimeScreen 由调用方映射成 token 字符串，HUD 交互屏映射为
     * `"hud_interaction"`（HUD 自己创建的屏，永远允许 HUD 渲染）。无屏时 token = null，
     * 按 whenScreen 模式区分默认值：
     *  - VISIBILITY_HIDE：屏存在则不渲染（与历史 skipForScreen 行为兼容）；无屏渲染
     *  - VISIBILITY_SHOW：任何屏都渲染；无屏也渲染
     *  - VISIBILITY_INTERACTION_ONLY：仅 hud_interaction 屏渲染；无屏不渲染
     *  - VISIBILITY_ALLOW_LIST：token 命中 allow 列表才渲染；无屏不渲染
     *  - VISIBILITY_DENY_LIST：token 不在 deny 列表才渲染；无屏渲染
     *
     * 注意：早先版本对 token=null 一律返回 true，导致 allow_list / interaction_only 在世界中
     * 也常驻显示，违反“显式只在某些屏出现”的语义。现在按 mode 区分 null 默认值。
     */
    fun shouldRender(currentScreenToken: String?): Boolean {
        if (HUD_INTERACTION_TOKEN == currentScreenToken) {
            return true
        }
        return when (whenScreen) {
            HudMetaDefaults.VISIBILITY_HIDE ->
                // 无屏 / 交互屏渲染，其他 vanilla 屏不渲染（与历史 skipForScreen 兼容）。
                currentScreenToken == null
            HudMetaDefaults.VISIBILITY_SHOW ->
                // 任意屏（含无屏）都渲染。
                true
            HudMetaDefaults.VISIBILITY_INTERACTION_ONLY ->
                // 仅交互屏（已在上面短路）；无屏 / 普通 vanilla 屏均不渲染。
                false
            HudMetaDefaults.VISIBILITY_ALLOW_LIST ->
                // 显式名单：只在 allow 命中时渲染；无屏视为未命中。
                currentScreenToken != null && allow.contains(currentScreenToken)
            HudMetaDefaults.VISIBILITY_DENY_LIST ->
                // 显式黑名单：除 deny 命中外都渲染；无屏视为未命中（即渲染）。
                currentScreenToken == null || !deny.contains(currentScreenToken)
            else -> false
        }
    }

    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other !is HudVisibilityPolicy) return false
        return whenScreen == other.whenScreen &&
            allow == other.allow &&
            deny == other.deny
    }

    // 逐位复刻 java.util.Objects.hash(whenScreen, allow, deny)：种子 1、31 倍累乘。
    override fun hashCode(): Int {
        var result = 1
        result = 31 * result + whenScreen.hashCode()
        result = 31 * result + allow.hashCode()
        result = 31 * result + deny.hashCode()
        return result
    }

    override fun toString(): String =
        "HudVisibilityPolicy{" +
            "whenScreen='" + whenScreen + '\'' +
            ", allow=" + allow +
            ", deny=" + deny +
            '}'

    companion object {
        /** HUD 交互屏专用 token：与 fabric VanillaScreenTokens 约定一致。 */
        const val HUD_INTERACTION_TOKEN: String = "hud_interaction"

        // @JvmField 使 Java 侧仍以 HudVisibilityPolicy.DEFAULT 静态字段访问。
        @JvmField
        val DEFAULT = HudVisibilityPolicy(
            HudMetaDefaults.VISIBILITY_HIDE,
            emptyList(),
            emptyList(),
        )

        // 防御式拷贝：入参为空返回空列表，逐元素非空校验，保序。
        private fun copy(values: List<String>?, label: String): List<String> {
            if (values.isNullOrEmpty()) {
                return emptyList()
            }
            val copied = ArrayList<String>(values.size)
            for (value in values) {
                copied.add(requireNotNull(value) { label })
            }
            return ReadOnlyList(copied)
        }
    }
}
